import { useState, useEffect, useCallback } from "react";
import { useAuth } from "store/AuthProvider";
import { useNetworkStatus } from "./useNetworkStatus";
import { usePendingQueue } from "./usePendingQueue";
import { tokenExpiredEvent } from "services/tokenExpiredEvent";

interface SessionState {
    isTokenExpired: boolean;
    needsReAuth: boolean;
    hasUnexpectedExit: boolean;
    pendingDataOnStartup: number;
    hasDataConflict: boolean;
    conflictDataCount: number;
}

/**
 * 会话管理 Hook
 * 处理 Token 过期、应用崩溃恢复等场景
 */
export const useSessionManager = () => {
    const auth = useAuth();
    const { isOnline } = useNetworkStatus();
    const { getPendingDataInfo } = usePendingQueue();
    
    const [sessionState, setSessionState] = useState<SessionState>({
        isTokenExpired: false,
        needsReAuth: false,
        hasUnexpectedExit: false,
        pendingDataOnStartup: 0,
        hasDataConflict: false,
        conflictDataCount: 0,
    });

    // B4: 检测应用是否异常退出（有待处理数据但没有正常退出标记）
    useEffect(() => {
        const checkUnexpectedExit = async () => {
            const normalExit = localStorage.getItem("normalExit");
            const pendingInfo = await getPendingDataInfo();
            
            if (!normalExit && pendingInfo.pendingCount > 0) {
                // 应用异常退出且有待处理数据
                setSessionState(prev => ({
                    ...prev,
                    hasUnexpectedExit: true,
                    pendingDataOnStartup: pendingInfo.pendingCount,
                }));
            }
            
            // 清除正常退出标记，等待下次退出时设置
            localStorage.removeItem("normalExit");
        };
        
        checkUnexpectedExit();
    }, [getPendingDataInfo]);

    // 标记正常退出
    useEffect(() => {
        const markNormalExit = () => {
            localStorage.setItem("normalExit", "true");
        };
        
        // 监听页面卸载事件
        window.addEventListener("beforeunload", markNormalExit);
        
        return () => {
            window.removeEventListener("beforeunload", markNormalExit);
        };
    }, []);

	// B2 & B3: 处理 Token 过期
	const handleTokenExpired = useCallback(async () => {
		const pendingInfo = await getPendingDataInfo();
        const cachedUser = auth.getLastCachedUser();
        
        // 保存会话过期信息到 localStorage
        localStorage.setItem("tokenExpired", "true");
        localStorage.setItem("tokenExpiredTime", new Date().toISOString());
        
        if (pendingInfo.pendingCount > 0) {
            // 有待处理数据，保存到 localStorage
            localStorage.setItem("pendingDataBeforeExpiry", JSON.stringify({
                count: pendingInfo.pendingCount,
                userId: pendingInfo.userId,
                userName: pendingInfo.userName,
            }));
        }
        
        if (isOnline) {
            // B2: 在线状态 - 提示重新登录但保留待处理数据
            setSessionState(prev => ({
                ...prev,
                isTokenExpired: true,
                needsReAuth: true,
            }));
        } else {
            // B3: 离线状态 - 允许继续操作，标记需要重新认证
            setSessionState(prev => ({
                ...prev,
                isTokenExpired: true,
                needsReAuth: false, // 离线时不立即要求重新认证
            }));
            
            // 如果有缓存用户，允许继续以离线模式操作
            if (cachedUser) {
                auth.resumeOfflineSession(cachedUser);
            }
        }
    }, [isOnline, getPendingDataInfo, auth]);

    // 检测 Token 过期标记（用于页面刷新后恢复状态）
    useEffect(() => {
        const tokenExpired = localStorage.getItem("tokenExpired");
        if (tokenExpired === "true") {
            setSessionState(prev => ({
                ...prev,
                isTokenExpired: true,
                needsReAuth: isOnline, // 在线时需要立即重新认证
            }));
        }
    }, [isOnline]);

    // 重新认证成功后清除过期状态
    const clearTokenExpiredState = useCallback(() => {
        localStorage.removeItem("tokenExpired");
        localStorage.removeItem("tokenExpiredTime");
        localStorage.removeItem("pendingDataBeforeExpiry");
        
        setSessionState(prev => ({
            ...prev,
            isTokenExpired: false,
            needsReAuth: false,
        }));
    }, []);

    // 确认已处理崩溃恢复
    const acknowledgeUnexpectedExit = useCallback(() => {
        setSessionState(prev => ({
            ...prev,
            hasUnexpectedExit: false,
        }));
    }, []);

	// 标记数据冲突
	const markDataConflict = useCallback((conflictCount: number) => {
		setSessionState(prev => ({
            ...prev,
            hasDataConflict: true,
            conflictDataCount: conflictCount,
        }));
    }, []);

    // 确认已处理数据冲突
    const acknowledgeDataConflict = useCallback(() => {
        setSessionState(prev => ({
            ...prev,
            hasDataConflict: false,
            conflictDataCount: 0,
        }));
    }, []);

    // 网络恢复时，如果 Token 已过期，提示重新认证
    useEffect(() => {
        if (isOnline && sessionState.isTokenExpired && !sessionState.needsReAuth) {
            setSessionState(prev => ({
                ...prev,
                needsReAuth: true,
            }));
        }
    }, [isOnline, sessionState.isTokenExpired, sessionState.needsReAuth]);

	// 监听 Token 过期事件
	useEffect(() => {
		const unsubscribe = tokenExpiredEvent.subscribe(() => {
			handleTokenExpired();
		});

		return () => {
			unsubscribe();
		};
	}, [handleTokenExpired]);

    // 监听测试事件（仅开发环境）
    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') return;

        const handleTestDataConflict = (event: CustomEvent) => {
            const { conflictCount } = event.detail;
            markDataConflict(conflictCount);
        };

        window.addEventListener('testDataConflict', handleTestDataConflict as EventListener);

        return () => {
            window.removeEventListener('testDataConflict', handleTestDataConflict as EventListener);
        };
    }, [markDataConflict]);

    return {
        sessionState,
        handleTokenExpired,
        clearTokenExpiredState,
        acknowledgeUnexpectedExit,
        markDataConflict,
        acknowledgeDataConflict,
    };
};

